import { useState, useRef, useCallback } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSidebar, toast } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry/helpers/capture';
import { ERROR_IDS } from '@/shared/constants/errorIds';

/**
 * Custom hook to manage sidebar hover-to-expand overlay behavior.
 *
 * Handles the state machine for:
 * - Temporary expansion on hover (with overlay shadow)
 * - Permanent pinning via background click
 * - Manual collapse via trigger button
 *
 * @param isCollapsed - Whether the sidebar is in collapsed state
 * @returns Object with state and event handlers for hover overlay behavior
 */
export function useSidebarHoverOverlay(
  isCollapsed: boolean,
  sidebarRef: React.RefObject<HTMLElement | null>,
) {
  const { setOpen } = useSidebar();

  /**
   * Tracks hover-based overlay expansion separately from persistent expansion.
   * When true, the sidebar is temporarily expanded via hover and should collapse
   * on mouse leave.
   */
  const [isHoverOverlay, setIsHoverOverlay] = useState(false);

  /**
   * Prevents hover expansion immediately after manual collapse via trigger button.
   * Reset on next mouse leave to allow hover expansion again.
   */
  const [preventHoverExpansion, setPreventHoverExpansion] = useState(false);

  /**
   * Tracks whether mouse is currently over the sidebar.
   * Used to evaluate whether to collapse when menu items are clicked.
   */
  const isMouseOverRef = useRef(false);

  /**
   * Count of currently open dropdowns (menu items, workspace switcher, user dropdown).
   * Sidebar should not collapse on mouse leave while any dropdown is open.
   */
  const openDropdownsRef = useRef(0);

  const handleMouseEnter = useCallback(() => {
    // Track that mouse is currently over sidebar
    isMouseOverRef.current = true;

    if (isCollapsed && !preventHoverExpansion) {
      // Show temporary overlay on hover
      setIsHoverOverlay(true);
      setOpen(true);
    }
  }, [isCollapsed, preventHoverExpansion, setOpen]);

  const handleMouseLeave = useCallback(() => {
    // Track that mouse is no longer over sidebar
    isMouseOverRef.current = false;

    // Reset the prevention flag when mouse leaves
    setPreventHoverExpansion(false);

    if (isHoverOverlay && openDropdownsRef.current === 0) {
      // Collapse when mouse leaves in overlay mode, but only if no dropdowns are open
      setOpen(false);
      setIsHoverOverlay(false);
    }
  }, [isHoverOverlay, setOpen]);

  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      try {
        // Only pin if clicking on non-interactive elements (background areas)
        const target = e.target as HTMLElement;

        // Validate target is an Element before DOM queries
        if (!target || !(target instanceof Element)) {
          return;
        }

        // Check if click is on an interactive element or its children
        const tagName = target.tagName?.toUpperCase();
        const isInteractiveElement =
          tagName === 'BUTTON' ||
          tagName === 'A' ||
          target.closest('button') ||
          target.closest('a') ||
          target.closest('[role="menuitem"]') ||
          target.closest('[role="button"]');

        // Pin the sidebar open permanently by exiting overlay mode
        // The sidebar remains expanded (setOpen(true) was called on hover)
        // but will no longer collapse on mouse leave
        if (isHoverOverlay && !isInteractiveElement) {
          setIsHoverOverlay(false);
        }
      } catch (error) {
        // Only catch expected DOM errors, let others bubble up
        if (
          error instanceof TypeError ||
          error instanceof DOMException ||
          (error instanceof Error &&
            (error.name === 'SecurityError' || error.name === 'NotFoundError'))
        ) {
          captureException(error, {
            tags: {
              component: 'useSidebarHoverOverlay',
              operation: 'handleBackgroundClick',
              errorId: ERROR_IDS.SIDEBAR_HOVER_BACKGROUND_CLICK_FAILED,
            },
          });

          // Provide user feedback
          toast.error(
            'Sidebar interaction failed. Please refresh if the issue persists.',
            { id: 'sidebar-background-click-error' },
          );

          // Attempt recovery: reset to safe state
          setIsHoverOverlay(false);
        } else {
          // Unexpected error type - re-throw to surface it
          throw error;
        }
      }
    },
    [isHoverOverlay],
  );

  const handleTriggerClick = useCallback(() => {
    // Reset hover overlay state when trigger is clicked
    // This allows the sidebar to collapse even when mouse is over it
    setIsHoverOverlay(false);
    // Prevent immediate re-expansion from hover
    setPreventHoverExpansion(true);
  }, []);

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    // Track number of open dropdowns to prevent collapsing while menus are open
    if (open) {
      openDropdownsRef.current += 1;
    } else {
      openDropdownsRef.current = Math.max(0, openDropdownsRef.current - 1);
    }
  }, []);

  // Handle clicks outside sidebar when in hover overlay mode
  // This complements handleMouseLeave - it handles cases where the user clicks
  // outside the sidebar without moving their mouse out of the sidebar bounds first.
  // Also handles clicks outside portal-rendered dropdown menus.
  useOnClickOutside(
    sidebarRef as React.RefObject<HTMLElement>,
    event => {
      // Only handle clicks when in hover overlay mode
      if (!isHoverOverlay) return;

      try {
        const target = event.target as HTMLElement;

        // Validate target is an Element before DOM queries
        if (!target || !(target instanceof Element)) {
          return;
        }

        // Check if click is inside a dropdown menu (rendered via portal)
        // Note: useOnClickOutside already checks if click is outside sidebarRef,
        // so we only need to check for dropdowns here
        const isInDropdown =
          target.closest('[role="menu"]') ||
          target.closest('[role="menuitem"]') ||
          target.closest('[data-radix-popper-content-wrapper]');

        if (isInDropdown) {
          return; // Click inside dropdown, do nothing
        }

        // Click is outside sidebar and dropdowns - collapse
        setOpen(false);
        setIsHoverOverlay(false);
        // Reset dropdown counter
        openDropdownsRef.current = 0;
      } catch (error) {
        // Only catch expected DOM errors, let others bubble up
        if (
          error instanceof TypeError ||
          error instanceof DOMException ||
          (error instanceof Error &&
            (error.name === 'SecurityError' || error.name === 'NotFoundError'))
        ) {
          captureException(error, {
            tags: {
              component: 'useSidebarHoverOverlay',
              operation: 'handleClickOutside',
              errorId: ERROR_IDS.SIDEBAR_HOVER_CLICK_OUTSIDE_FAILED,
            },
            extra: {
              eventType: event.type,
              targetNodeName: (event.target as Node)?.nodeName,
            },
          });

          // Provide user feedback
          toast.error(
            'Sidebar interaction failed. Please refresh if the issue persists.',
            { id: 'sidebar-click-outside-error' },
          );

          // Attempt recovery: reset to safe state
          setOpen(false);
          setIsHoverOverlay(false);
          openDropdownsRef.current = 0;
        } else {
          // Unexpected error type - re-throw to surface it
          throw error;
        }
      }
    },
    'mousedown',
  );

  // Handle Escape key to collapse sidebar when in hover overlay mode
  // This is critical for keyboard accessibility - users who can't use hover
  // need a way to collapse the sidebar without clicking outside
  useHotkeys(
    'esc',
    () => {
      // Collapse sidebar on Escape
      setOpen(false);
      setIsHoverOverlay(false);
      // Reset dropdown counter
      openDropdownsRef.current = 0;
    },
    {
      enabled: isHoverOverlay,
      preventDefault: true,
    },
  );

  return {
    isHoverOverlay,
    handleMouseEnter,
    handleMouseLeave,
    handleBackgroundClick,
    handleTriggerClick,
    handleDropdownOpenChange,
  };
}
